import React, { Component } from 'react'
import PropTypes from 'prop-types'
import CarouselContainer from '../Carousel/CarouselContainer'
import AlternativesHeadline from './components/AlternativesHeadline'
import {
  fireRecommendationAnalyticsNewPage,
  fireRecommendationBlockViewed,
  fireRecommendationBlockLoaded,
  getAnalyticsData
} from '../../helpers/recommendationsAnalytics'
import updateDigitalData, { pushEventAndTrack } from '../../helpers/taggingSerializer'
import itemsOnDisplayInSlider from '../../helpers/itemsOnDisplayInSlider'
import AlternativesSkeleton from './components/Alternatives.Skeleton'
import { useFeatureFlagContext } from '@/javascript/providers/FeatureFlagContext/FeatureFlagContext'

import * as Styled from './Alternatives.styles'
import Box from '@/components/Box/Box'

const BRAND = process.env.BRAND || 'ARGOS'

class Alternatives extends Component {
  constructor(props) {
    super(props)
    this.state = {
      currentIndex: -1
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.hasAlternativesRequestDone &&
      this.props.hasAlternativesRequestDone !== prevProps.hasAlternativesRequestDone
    ) {
      setTimeout(() => {
        pushEventAndTrack({
          eventInfo: {
            eventName: 'pdp_requestRecommendations',
            eventAction: 'Request Recommendations'
          },
          attributes: {
            recPlacement: this.props.type,
            recResponse: this.props.products.length > 0 && this.props.hasProducts !== false ? '1' : '0'
          }
        })
      }, 1000)
    }
  }

  updateCurrentIndex = carousel => {
    const { state = {} } = carousel || {}
    const { currentIndex = 0 } = state
    this.setState({
      currentIndex
    })
    return currentIndex
  }

  onViewportChange = inView => {
    if (!inView || !this.carousel) return
    const { products, type: source, headline, mvts } = this.props

    setTimeout(() => {
      if (!products.length) return
      const displayedList = this.partNumbersOnDisplay(this.carousel)
      const analyticsData = getAnalyticsData({
        products,
        headline,
        displayedList,
        source,
        mvts,
        pageIndex: this.state.currentIndex
      })
      fireRecommendationBlockViewed(analyticsData)
    }, 4000)
  }

  partNumbersOnDisplay = carousel => {
    const displayedItems = itemsOnDisplayInSlider(carousel)
    const displayedProducts = displayedItems
      .map(item => item.props)
      .map(props => React.Children.only(props.children))
      .map(child => child.props)
      .map(props => props.partNumber)
    return displayedProducts
  }

  onSliderUpdated = () => {
    const { carousel, updateCurrentIndex, props } = this
    updateCurrentIndex(carousel)
    const stores = {
      productStore: props.productStore,
      bazaarVoiceReviewStore: props.bazaarVoiceReviewStore,
      bazaarVoiceQuestionAnswerStore: props.bazaarVoiceQuestionAnswerStore,
      storeLocatorStore: props.storeLocator,
      alternativeProductStore: props.alternativesStore,
      featuredProductsStore: props.featuredProductsStore,
      specialOffersCreatorStore: props.specialOffersCreator,
      shopCollection: props.shopCollection
    }
    updateDigitalData(stores)
  }

  onSlideChanged = () => {
    const { carousel, props, updateCurrentIndex, partNumbersOnDisplay } = this
    const pageIndex = updateCurrentIndex(carousel)
    const { products, headline, parentPartNumber, mvts, updateAlternatives, type: source } = props
    const displayedList = partNumbersOnDisplay(carousel)
    const analyticsData = getAnalyticsData({ products, headline, displayedList, source, mvts, pageIndex })
    fireRecommendationAnalyticsNewPage(analyticsData)

    if (displayedList.length) {
      const payload = {
        partNumber: parentPartNumber,
        data: {
          displayedAlternativeProducts: displayedList.join(',')
        }
      }
      updateAlternatives(payload)
    }
  }

  onSliderLoaded = () => {
    const { onSliderUpdated, updateCurrentIndex, carousel, props, partNumbersOnDisplay } = this
    onSliderUpdated()
    updateCurrentIndex(carousel)
    setTimeout(() => {
      const { products, headline, mvts, type: source } = props
      const displayedList = partNumbersOnDisplay(carousel)
      const analyticsData = getAnalyticsData({
        products,
        headline,
        displayedList,
        source,
        mvts,
        pageIndex: this.state.currentIndex
      })
      fireRecommendationBlockLoaded(analyticsData)
    }, 4000)
  }

  render() {
    const {
      headline,
      parentPartNumber,
      products,
      hasProducts,
      hasAllProductsErrors,
      hasAlternativesRequestDone,
      hasAlternativesInCategory,
      onClient,
      type: source,
      hasContainer,
      PDP_V2_FLAG
    } = this.props

    if (this.props.setNoOHATRecsExist && !products.length && hasAlternativesRequestDone) {
      this.props.setNoOHATRecsExist()
    }
    if (!onClient || (products.length === 0 && hasAlternativesRequestDone) || hasAllProductsErrors) return null
    const refFun = carousel => {
      this.carousel = carousel
    }
    const { onViewportChange, onSlideChanged, onSliderLoaded, onSliderUpdated } = this
    const { currentIndex } = this.state
    const analyticsData = getAnalyticsData({ products, headline, source, pageIndex: currentIndex })

    const itemsPerSlide = {
      itemsPerSlideXs: 3,
      itemsPerSlideSm: 3,
      itemsPerSlideMd: 3,
      itemsPerSlideLg: source === 'avAlternatives' ? 3 : 4
    }

    const options = {
      refFun,
      parentPartNumber,
      products,
      onViewportChange,
      onSlideChanged,
      onSliderLoaded,
      onSliderUpdated,
      analyticsData,
      itemsPerSlide,
      isNofollowLink: BRAND === 'ARGOS'
    }

    const alternatives = (
      <Styled.Container
        PDP_V2_FLAG={PDP_V2_FLAG}
        className={`${hasContainer ? 'container' : ''}`}
        data-test={`product-${source}`}
      >
        {!hasProducts && !hasAlternativesRequestDone && <AlternativesSkeleton />}
        {hasAlternativesInCategory && (
          <>
            <AlternativesHeadline hasProducts={hasProducts} headline={headline} />
            <CarouselContainer {...options} data-test='carousel-container' />
          </>
        )}
      </Styled.Container>
    )
    return source === 'avAlternatives' ? <Box className='pdp-core'>{alternatives}</Box> : alternatives
  }
}

Alternatives.propTypes = {
  products: PropTypes.array,
  parentPartNumber: PropTypes.string,
  headline: PropTypes.string,
  mvts: PropTypes.array,
  hasContainer: PropTypes.bool,
  PDP_V2_FLAG: PropTypes.bool
}

Alternatives.defaultProps = {
  headline: '...Or how about these?',
  hasContainer: true,
  mvts: [],
  PDP_V2_FLAG: false
}

const FlaggedAlternatives = props => {
  const { PDP_V2_FLAG } = useFeatureFlagContext()
  return <Alternatives {...props} PDP_V2_FLAG={PDP_V2_FLAG} />
}

export { Alternatives }
export default FlaggedAlternatives
